import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  Pool,
  PlayerPick,
  NFL_NO_REPEAT_POSITIONS,
  NFL_NO_REPEAT_TOTAL_WEEKS,
} from '../types/pool';
import FeeNotice from '../components/FeeNotice';
import {
  NflPlayerPicker,
  matchupLabel,
  isLockedNow,
  type AvailablePlayer,
  type PickerOption,
} from '../components/NflPlayerPicker';

// The seven slots in display order, with labels + eligibility from the shared config.
const SLOTS = Object.keys(NFL_NO_REPEAT_POSITIONS) as (keyof typeof NFL_NO_REPEAT_POSITIONS)[];

type WeekLineup = Record<string, PlayerPick | null>;

/** A player already burned in a submitted lineup, with the week he was used. */
interface UsedPlayer {
  playerId: string;
  playerName: string;
  position: string;
  teamId: string;
  week: number;
  slot: string;
}

/** Flatten the all-weeks response into a week-ordered used-player list. */
function usedFromWeeks(weeks: any[]): UsedPlayer[] {
  const out: UsedPlayer[] = [];
  for (const w of Array.isArray(weeks) ? weeks : []) {
    for (const s of w.slots || []) {
      if (s.pick?.playerId) {
        out.push({
          playerId: s.pick.playerId,
          playerName: s.pick.playerName,
          position: s.pick.position,
          teamId: s.pick.teamId,
          week: w.week,
          slot: s.slot,
        });
      }
    }
  }
  return out.sort((a, b) => a.week - b.week || SLOTS.indexOf(a.slot as (typeof SLOTS)[number]) - SLOTS.indexOf(b.slot as (typeof SLOTS)[number]));
}

function emptyLineup(): WeekLineup {
  const l: WeekLineup = {};
  for (const slot of SLOTS) l[slot] = null;
  return l;
}

// Compact relative kickoff label.
function kickoffLabel(kickoff: string | null, locked: boolean): string {
  if (!kickoff) return 'No game scheduled';
  const d = new Date(kickoff);
  const local = d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (locked) return `🔒 Kicked off ${local}`;
  const diffMs = d.getTime() - Date.now();
  const hrs = Math.floor(diffMs / 3_600_000);
  if (hrs < 24) return `Locks ${local} (in ${hrs}h)`;
  const days = Math.floor(hrs / 24);
  return `Locks ${local} (in ${days}d)`;
}

export function NflNoRepeatEntryForm() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null>(null);
  const [week, setWeek] = useState(1);
  // Multi-entry: the user's entries in this pool. activeEntryId null = drafting a new entry.
  const [myEntries, setMyEntries] = useState<{ id: string; entryName: string | null }[]>([]);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [seasonScore, setSeasonScore] = useState(0);
  const [weekScores, setWeekScores] = useState<Record<number, number>>({});
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [lineup, setLineup] = useState<WeekLineup>(emptyLineup());

  const [entryName, setEntryName] = useState('');

  const [loading, setLoading] = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Public per-week standings (respecting reveal)
  const [showResults, setShowResults] = useState(false);
  const [weekLineups, setWeekLineups] = useState<any | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  // All players burned across the season (with the week each was used)
  const [usedPlayers, setUsedPlayers] = useState<UsedPlayer[]>([]);
  const [showUsed, setShowUsed] = useState(true);

  // Which slot's player picker is open (null = closed)
  const [pickerSlot, setPickerSlot] = useState<(typeof SLOTS)[number] | null>(null);

  // Re-render every 30s so kickoff countdowns tick and slots lock live at kickoff.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Map a backend slot pick (which is a PlayerPick) into our working lineup.
  const lineupFromWeeks = useCallback((weeks: any[], wk: number): WeekLineup => {
    const l = emptyLineup();
    const target = Array.isArray(weeks) ? weeks.find((w) => w.week === wk) : null;
    if (target && Array.isArray(target.slots)) {
      for (const s of target.slots) {
        if (s.pick && s.slot) l[s.slot] = s.pick as PlayerPick;
      }
    }
    return l;
  }, []);

  // Initial load: pool + my entry (if any).
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }
    let cancelled = false;
    (async () => {
      if (!poolId) return;
      try {
        setLoading(true);
        setError(null);
        const poolData = await api.getPool(poolId);
        if (cancelled) return;
        setPool(poolData);

        const token = await getAccessTokenSilently();
        try {
          const mine = await api.getMyEntries(poolData.id, token);
          if (cancelled) return;
          const list = (Array.isArray(mine) ? mine : []).map((e: any) => ({
            id: e.id,
            entryName: e.entryName ?? null,
          }));
          setMyEntries(list);
          setActiveEntryId(list.length ? list[0].id : null);
        } catch {
          // Not joined yet — start with an empty week 1.
          if (cancelled) return;
          setMyEntries([]);
          setActiveEntryId(null);
          setLineup(emptyLineup());
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load pool');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId, isAuthenticated, getAccessTokenSilently, loginWithRedirect, lineupFromWeeks]);

  // Season totals + burned players for the active entry.
  const loadEntryData = useCallback(
    async (entryId: string) => {
      if (!pool) return;
      try {
        const token = await getAccessTokenSilently();
        const mine = await api.getMyWeeklyLineup(pool.id, token, undefined, entryId);
        setSeasonScore(Number(mine.seasonScore) || 0);
        const scores: Record<number, number> = {};
        for (const w of mine.weeks || []) scores[w.week] = Number(w.weekScore) || 0;
        setWeekScores(scores);
        setUsedPlayers(usedFromWeeks(mine.weeks || []));
      } catch {
        // Best-effort — the week view still loads on its own.
      }
    },
    [pool, getAccessTokenSilently],
  );

  useEffect(() => {
    if (activeEntryId) {
      loadEntryData(activeEntryId);
    } else {
      setSeasonScore(0);
      setWeekScores({});
      setUsedPlayers([]);
    }
  }, [activeEntryId, loadEntryData]);

  // Load the selected week's available players + the active entry's saved lineup.
  const loadWeek = useCallback(
    async (wk: number) => {
      if (!pool) return;
      try {
        setWeekLoading(true);
        setError(null);
        setShowResults(false);
        setWeekLineups(null);
        const token = await getAccessTokenSilently();
        const players = await api.getAvailableWeeklyPlayers(
          pool.id,
          wk,
          token,
          activeEntryId ?? 'new',
        );
        setAvailablePlayers(Array.isArray(players) ? players : []);
        if (activeEntryId) {
          const mine = await api.getMyWeeklyLineup(pool.id, token, wk, activeEntryId);
          setLineup(lineupFromWeeks([mine.week], wk));
        } else {
          setLineup(emptyLineup());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load week');
      } finally {
        setWeekLoading(false);
      }
    },
    [pool, activeEntryId, getAccessTokenSilently, lineupFromWeeks],
  );

  useEffect(() => {
    if (pool) loadWeek(week);
  }, [pool, week, loadWeek]);

  const playersById = useMemo(() => {
    const m = new Map<string, AvailablePlayer>();
    for (const p of availablePlayers) m.set(p.playerId, p);
    return m;
  }, [availablePlayers]);

  // Is the player currently in this slot locked (his game has started)?
  function slotLocked(slot: string): boolean {
    const pick = lineup[slot];
    if (!pick) return false;
    const ap = playersById.get(pick.playerId);
    return ap ? isLockedNow(ap) : false;
  }

  function handleSelect(slot: string, playerId: string) {
    if (slotLocked(slot)) return;
    setSuccess(null);
    if (!playerId) {
      setLineup((prev) => ({ ...prev, [slot]: null }));
      return;
    }
    const p = playersById.get(playerId);
    if (!p) return;
    setLineup((prev) => ({
      ...prev,
      [slot]: {
        playerId: p.playerId,
        playerName: p.playerName,
        teamId: p.teamId,
        position: p.position,
      },
    }));
  }

  // Players eligible + selectable for a slot, with disabled flags for the picker.
  function optionsForSlot(slot: keyof typeof NFL_NO_REPEAT_POSITIONS): PickerOption[] {
    const eligible = NFL_NO_REPEAT_POSITIONS[slot].eligible as readonly string[];
    const selectedElsewhere = new Set<string>();
    for (const s of SLOTS) {
      if (s !== slot && lineup[s]) selectedElsewhere.add(lineup[s]!.playerId);
    }
    const usedWeek = new Map(usedPlayers.map((u) => [u.playerId, u.week]));
    const current = lineup[slot]?.playerId;
    return availablePlayers
      .filter((p) => eligible.includes(p.position))
      .map((p) => {
        const isCurrent = p.playerId === current;
        const dupThisWeek = selectedElsewhere.has(p.playerId);
        const lockedNow = isLockedNow(p);
        const disabled = !isCurrent && (p.used || lockedNow || dupThisWeek);
        let reason: string | null = null;
        if (p.used) reason = usedWeek.has(p.playerId) ? `used Wk ${usedWeek.get(p.playerId)}` : 'used another week';
        else if (lockedNow) reason = '🔒 kicked off';
        else if (dupThisWeek) reason = 'already in lineup';
        return { player: p, disabled, reason };
      });
  }

  const filledCount = useMemo(() => SLOTS.filter((s) => lineup[s]).length, [lineup]);

  const hasEntryFee = !!(pool && pool.entryFee && Number(pool.entryFee) > 0);

  // New entries can be created only while the pool is open and before the
  // deadline. Existing entries stay editable all season (per-player locks).
  const joinOpen =
    !!pool &&
    pool.status === 'OPEN' &&
    (!pool.deadline || new Date(pool.deadline).getTime() > Date.now());
  const creating = activeEntryId === null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pool) return;
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      const token = await getAccessTokenSilently();

      const positions: Record<string, PlayerPick> = {};
      for (const slot of SLOTS) {
        const pick = lineup[slot];
        if (pick) positions[slot] = pick;
      }

      if (!activeEntryId) {
        // Create a new entry (first join, or an additional entry) with this
        // week's lineup as the initial picks.
        const usage: Record<string, { weeks: number[]; count: number }> = {};
        for (const pick of Object.values(positions)) {
          usage[pick.playerId] = { weeks: [week], count: 1 };
        }
        const entryData: any = {
          entryName: entryName || undefined,
          picks: {
            type: 'WEEKLY_LINEUP',
            weeks: { [String(week)]: { positions, weekScore: 0 } },
            playerUsageHistory: usage,
          },
        };
        const created = await api.createEntry(pool.id, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay.
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool.name },
          });
          return;
        }
        const mine = await api.getMyEntries(pool.id, token);
        const list = (Array.isArray(mine) ? mine : []).map((e: any) => ({
          id: e.id,
          entryName: e.entryName ?? null,
        }));
        setMyEntries(list);
        setActiveEntryId((created as any)?.id || (list.length ? list[list.length - 1].id : null));
        setEntryName('');
        setSuccess(`Entry created — Week ${week} lineup saved.`);
        return;
      }

      await api.submitWeeklyLineup(pool.id, { week, positions, entryId: activeEntryId }, token);

      // Refresh season totals + this week.
      await loadEntryData(activeEntryId);
      setSuccess(`Week ${week} lineup saved.`);
      await loadWeek(week);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save lineup');
    } finally {
      setSubmitting(false);
    }
  }

  async function loadResults() {
    if (!pool) return;
    try {
      setResultsLoading(true);
      const data = await api.getWeekLineups(pool.id, week);
      setWeekLineups(data);
      setShowResults(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load weekly results');
    } finally {
      setResultsLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error && !pool) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
        <Link to="/pools" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
          ← Back to Pools
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link
          to={`/pools/${poolId}`}
          className="text-blue-600 hover:text-blue-800 mb-2 inline-block"
        >
          ← Back to {pool?.name}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">
          {!creating
            ? 'Set Your Weekly Lineup'
            : myEntries.length
              ? 'New Entry — NFL No-Repeat'
              : 'Join — NFL No-Repeat'}
        </h1>
        <p className="text-gray-600 mt-1">
          Pick a 7-player offense lineup each week. Any player you use is burned for the rest of the
          season. Each player locks at his own kickoff.
        </p>
        {!creating && (
          <p className="text-sm text-gray-700 mt-2">
            Season total:{' '}
            <span className="font-semibold text-emerald-700">{seasonScore.toFixed(1)} pts</span>
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700 mb-4">
          {success}
        </div>
      )}

      {/* Week selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Week</span>
          {weekScores[week] !== undefined && (
            <span className="text-sm text-gray-500">
              Week {week} score:{' '}
              <span className="font-semibold text-emerald-700">
                {(weekScores[week] || 0).toFixed(1)}
              </span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-9 gap-2">
          {Array.from({ length: NFL_NO_REPEAT_TOTAL_WEEKS }, (_, i) => i + 1).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWeek(w)}
              className={`py-2 rounded-md text-sm font-medium transition-colors ${
                w === week
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lineup builder */}
          <div className="lg:col-span-2 space-y-3">
            {myEntries.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">My Entries</span>
                  {joinOpen && !creating && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveEntryId(null);
                        setEntryName('');
                        setSuccess(null);
                        setError(null);
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      + New Entry
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {myEntries.map((e, i) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setActiveEntryId(e.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        e.id === activeEntryId
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {e.entryName || `Entry ${i + 1}`}
                    </button>
                  ))}
                  {creating && (
                    <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                      New entry…
                    </span>
                  )}
                </div>
              </div>
            )}

            {creating && !joinOpen && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                New entries are closed — the entry deadline has passed.
                {myEntries.length > 0 && ' You can still manage your existing entries all season.'}
              </div>
            )}

            {creating && joinOpen && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label htmlFor="entryName" className="block text-sm font-medium text-gray-700 mb-1">
                  Entry Name (optional)
                </label>
                <input
                  id="entryName"
                  type="text"
                  value={entryName}
                  onChange={(e) => setEntryName(e.target.value)}
                  placeholder="My Entry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {weekLoading ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                Loading week {week}…
              </div>
            ) : (
              SLOTS.map((slot) => {
                const cfg = NFL_NO_REPEAT_POSITIONS[slot];
                const pick = lineup[slot];
                const locked = slotLocked(slot);
                const kickoff = pick ? (playersById.get(pick.playerId)?.kickoff ?? null) : null;
                return (
                  <div
                    key={slot}
                    className={`rounded-lg border-2 p-4 transition-all ${
                      pick ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-semibold text-gray-900">{cfg.label}</span>
                        <span className="ml-2 text-xs text-gray-500">{slot}</span>
                      </div>
                      {locked && (
                        <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                          🔒 Locked
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => setPickerSlot(slot)}
                      className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between gap-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                        locked
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
                          : 'bg-white border-gray-300 hover:border-emerald-400'
                      }`}
                    >
                      <span className={`truncate ${pick ? 'text-gray-900' : 'text-gray-400'}`}>
                        {pick
                          ? (() => {
                              const ap = playersById.get(pick.playerId);
                              const team = ap?.teamShortName || ap?.teamName;
                              return `${pick.playerName}${team ? ` (${team})` : ''}${ap ? ` ${matchupLabel(ap)}` : ''}`;
                            })()
                          : '— Empty (scores 0) —'}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {pick && (
                      <p className="text-xs text-gray-500 mt-1">
                        {(() => {
                          const ap = playersById.get(pick.playerId);
                          const matchup = ap ? `${matchupLabel(ap)} · ` : '';
                          return `${matchup}${kickoffLabel(kickoff, locked)}`;
                        })()}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Sidebar: progress, payment, submit */}
          <div className="lg:sticky lg:top-4 space-y-4 self-start">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Week {week} filled</span>
                <span className="text-sm text-gray-500">{filledCount} / 7</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${(filledCount / 7) * 100}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Empty slots are allowed — they simply score 0.
              </p>
            </div>

            {/* Used Players — every player burned so far, by week */}
            {!creating && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowUsed(!showUsed)}
                  className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 text-sm">
                    🔥 Used Players ({usedPlayers.length})
                  </h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${showUsed ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showUsed && (
                  <div className="p-3 max-h-72 overflow-y-auto">
                    {usedPlayers.length === 0 ? (
                      <p className="text-xs text-gray-500">No players used yet — your full pool is available.</p>
                    ) : (
                      <ul className="space-y-1">
                        {usedPlayers.map((u) => {
                          const team = playersById.get(u.playerId)?.teamShortName;
                          const isThisWeek = u.week === week;
                          return (
                            <li
                              key={`${u.week}-${u.slot}-${u.playerId}`}
                              className={`flex items-center justify-between text-xs rounded px-2 py-1 ${
                                isThisWeek ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'
                              }`}
                            >
                              <span className="truncate">
                                {u.playerName}
                                <span className="text-gray-400"> · {u.position}{team ? ` · ${team}` : ''}</span>
                              </span>
                              <span className={`ml-2 flex-shrink-0 font-medium ${isThisWeek ? 'text-emerald-700' : 'text-gray-500'}`}>
                                Wk {u.week}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <p className="text-[11px] text-gray-400 mt-2">
                      Burned players can't be used again this season. This week's picks stay editable until each player's kickoff.
                    </p>
                  </div>
                )}
              </div>
            )}

            {hasEntryFee && creating && joinOpen && (
              <FeeNotice entryFee={Number(pool!.entryFee)} />
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <button
                type="submit"
                disabled={submitting || weekLoading || (creating && !joinOpen)}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting
                  ? 'Saving…'
                  : !creating
                    ? `Save Week ${week}`
                    : myEntries.length
                      ? 'Create Entry'
                      : 'Join Pool'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Editable slots save instantly; locked slots are left untouched.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate(`/pools/${pool?.slug || poolId}`)}
              className="block w-full text-center text-gray-600 hover:text-gray-900"
            >
              Done
            </button>
          </div>
        </div>
      </form>

      {/* Public per-week standings (respecting reveal) */}
      <div className="mt-8">
        <button
          type="button"
          onClick={loadResults}
          disabled={resultsLoading}
          className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {resultsLoading ? 'Loading…' : `View all entrants — Week ${week}`}
        </button>

        {showResults && weekLineups && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Week {week} — All Entrants</h3>
            <p className="text-sm text-amber-700">
              Each pick is revealed when that player's game kicks off — unplayed
              picks stay hidden until then
              {!weekLineups.revealed && weekLineups.revealAt
                ? ` (Week ${week}'s first games start ${new Date(weekLineups.revealAt).toLocaleString()})`
                : ''}
              . Weekly scores are always visible.
            </p>
            <div className="space-y-3 mt-2">
              {(weekLineups.lineups || []).map((lu: any) => (
                <div key={lu.entryId} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">
                      {lu.entryName ||
                        lu.user?.username ||
                        `${lu.user?.firstName || ''} ${lu.user?.lastName || ''}`.trim() ||
                        'Entry'}
                    </span>
                    <span className="text-sm text-emerald-700 font-semibold">
                      {Number(lu.weekScore || 0).toFixed(1)} pts
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {SLOTS.map((slot) => {
                      const p = lu.positions?.[slot] as PlayerPick | undefined;
                      const isHidden = Array.isArray(lu.hiddenSlots) && lu.hiddenSlots.includes(slot);
                      return (
                        <span
                          key={slot}
                          className={`text-xs px-2 py-1 rounded ${
                            isHidden
                              ? 'bg-gray-50 text-gray-400 italic border border-dashed border-gray-200'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {slot}: {p ? p.playerName : isHidden ? '🔒 at kickoff' : '—'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Player picker modal */}
      {pickerSlot && !slotLocked(pickerSlot) && (
        <NflPlayerPicker
          slotLabel={NFL_NO_REPEAT_POSITIONS[pickerSlot].label}
          options={optionsForSlot(pickerSlot)}
          currentPlayerId={lineup[pickerSlot]?.playerId || null}
          onSelect={(playerId) => {
            handleSelect(pickerSlot, playerId || '');
            setPickerSlot(null);
          }}
          onClose={() => setPickerSlot(null)}
        />
      )}
    </div>
  );
}
